import {getWSUrl} from '@/api/utils'
import {useFrame} from '@react-three/fiber'
import AudioManager from '@repo/shared/AudioManager'
import {
  ESprintenTeam,
  SprintenTeamsLogoMap,
} from '@repo/shared/Customer/Coop/SprintenTeams/SprintenTeams.types'
import {toastMessage} from '@repo/shared/Toast'
import useWebsocket from '@repo/shared/WebSocketHook/useWebsocket'
import clsx from 'clsx'
import {useAtom, useSetAtom} from 'jotai'
import {useEffect, useRef} from 'react'
import {EScene} from '../../types/scene.types'
import {
  EMessageType,
  IEndPositionMessage,
  IErrorMessage,
  ILeaderboardMessage,
  TWebSocketMessage,
} from '../../types/ws.types'
import FrameStore from '../Store/FrameStore'
import {
  endPositonsState,
  gameCodeState,
  gameEndedState,
  gameStartedState,
  IEndPositionsState,
  sceneState,
  timestampGameEndedState,
  timestampGameStartedState,
  totalPlayersState,
} from '../Store/atoms'

const fStore = new FrameStore()
const audioManager = new AudioManager()

export const GameData = () => {
  const [gameCode, setGameCode] = useAtom(gameCodeState)
  const [scene, setScene] = useAtom(sceneState)
  const setGameEnded = useSetAtom(gameEndedState)
  const setGameStarted = useSetAtom(gameStartedState)
  const setGameStartedTimestamp = useSetAtom(timestampGameStartedState)
  const setGameEndedTimestamp = useSetAtom(timestampGameEndedState)
  const [totalPlayers, setTotalPlayers] = useAtom(totalPlayersState)
  const [endPositions, setEndPositions] = useAtom(endPositonsState)

  // helper refs to determine the actual finished state of the game
  const isFinished = useRef(false)
  const isFinishedTimestamp = useRef<number | null>(null)

  const socket = useWebsocket<TWebSocketMessage>()

  /**
   * Connect to the WebSocket server if we have a game code
   */
  useEffect(() => {
    if (gameCode) {
      socket.connect(getWSUrl(gameCode)).catch(() => {
        socket.disconnect()
      })
    } else {
      socket.disconnect()
    }

    return () => {
      socket.disconnect()
    }
  }, [gameCode])

  const updateEndPositions = (endPosition: IEndPositionsState) => {
    fStore.appendEndPositions(endPosition)
    setEndPositions(prev => [...prev, endPosition])
  }

  const prevLeaderboard = useRef<ILeaderboardMessage | null>(null)

  /**
   * Listen for incoming messages and update the game state
   */
  useEffect(() => {
    const unsubscribe = socket.subscribeMessage(message => {
      switch (message?.type) {
        case EMessageType.Info:
          fStore.setInfo(message)

          // reset local refs
          isFinished.current = false
          isFinishedTimestamp.current = null
          prevLeaderboard.current = null

          if (message.hasStarted) {
            setScene(EScene.STARTED)
          } else {
            setGameStartedTimestamp(null)
            setScene(EScene.LOBBY)
          }
          break

        case EMessageType.Leaderboard:
          // Update total players
          if (message.totalClients !== totalPlayers) setTotalPlayers(message.totalClients)

          fStore.setLeaderboard(message)

          const currentLeaderboardTeams = (message as ILeaderboardMessage).leaderboardTeams
          const prevLeaderboardTeams = prevLeaderboard.current?.leaderboardTeams ?? []

          const isTeamsReduced = currentLeaderboardTeams.length < prevLeaderboardTeams.length

          // Notify if a team has dropped out
          if (
            currentLeaderboardTeams.length !== 5 &&
            prevLeaderboard.current &&
            message &&
            isTeamsReduced
          ) {
            let teamsThatLeft = prevLeaderboardTeams.filter(obj =>
              currentLeaderboardTeams.every(s => s.team !== obj.team),
            )

            teamsThatLeft.forEach(team => {
              toastMessage(t => (
                <div className="flex gap-[8px]">
                  <div
                    className={clsx(
                      'rounded-[4px] p-[4px]',
                      team.team === ESprintenTeam.Anglamark && 'bg-team-anglamark',
                      team.team === ESprintenTeam.CoopMega && 'bg-team-coop_mega',
                      team.team === ESprintenTeam.CoopPrix && 'bg-team-coop_prix',
                      team.team === ESprintenTeam.Extra && 'bg-team-extra',
                      team.team === ESprintenTeam.OBS && 'bg-team-obs',
                    )}
                  >
                    <img
                      src={SprintenTeamsLogoMap[team.team]}
                      alt=""
                      className="h-[30px] w-[30px]"
                    />
                  </div>
                  <div className="flex items-center">forlot spillet</div>
                </div>
              ))
            })
          }

          prevLeaderboard.current = message

          break

        case EMessageType.GameStarted:
          setGameStarted(message)
          setScene(EScene.STARTED_COUNTDOWN)
          break

        case EMessageType.EndPosition:
          const endPositionData: IEndPositionsState = {
            ...message,
            teamEndTimestamp: Date.now(),
          }

          const _m = message as IEndPositionMessage

          updateEndPositions(endPositionData)
          break

        case EMessageType.GameEnded:
          setGameEnded(message)
          setGameEndedTimestamp(Date.now())
          fStore.setShouldSoftEnd()
          socket.disconnect()
          break
        case EMessageType.Error:
          const error = message as IErrorMessage
          toastMessage.error(error.message)
          setGameCode(null)
          setScene(EScene.PRE_LOBBY)
          break

        case EMessageType.Pong:
          break

        default:
          console.warn('Unknown message type', message)
          break
      }
    })

    return () => unsubscribe()
  }, [socket])

  useFrame(() => {
    // wait a few seconds for allowing everyone to cross the finish line

    if (fStore.getShouldSoftEnd() && !isFinished.current) {
      isFinishedTimestamp.current = !isFinishedTimestamp.current
        ? Date.now()
        : isFinishedTimestamp.current

      const waitTime = (Date.now() - isFinishedTimestamp.current) / 1000

      if (waitTime > 2) {
        isFinished.current = true
        setScene(EScene.SCOREBOARD)
        audioManager.BGM.ingame?.fade(audioManager.BGM.ingame.volume(), 1, 5000)
      }
    }
  })

  return null
}
